if (!customElements.get('hero-slider-carousel')) {
  class HeroSliderCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-goto]'));
      this.prevBtn = this.querySelector('.hero-slider__arrow--prev');
      this.nextBtn = this.querySelector('.hero-slider__arrow--next');
      this.track = this.querySelector('.hero-slider__track');
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = (parseFloat(this.dataset.autoplaySpeed) || 5) * 1000;
      this.initialAutoplayDelay = Math.max(this.speed * 6, 45000);
      this.timer = null;
      this.initialAutoplayTimer = null;
      this.autoplayReady = false;
      this.isPaused = false;

      if (this.slides.length < 2) return;

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.go(this.index + 1));
      }
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

      this.addEventListener('mouseenter', () => {
        this.isPaused = true;
        this.stop();
      });
      this.addEventListener('mouseleave', () => {
        this.isPaused = false;
        this.start();
      });

      this.setupDrag();
      this.setupWheel();
      this.loadSlide(0);
      /* The first slide's image loads eagerly (good for LCP), but if it's a
         video slide, starting the video download immediately would compete
         with the page's critical resources and slow down initial load on
         mobile. Defer it until after window 'load' instead — the poster
         stays visible until then, then the video takes over. */
      if (this.slides[0] && this.slides[0].querySelector('video[data-hero-lazy-video]')) {
        this.deferFirstVideo();
      } else {
        this.activateVideo(0);
      }
      this.deferAdjacentSlide();

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const idx = Number(e.target && e.target.dataset ? e.target.dataset.index : NaN);
          if (!Number.isNaN(idx)) {
            this.stop();
            this.go(idx);
          }
        });
        document.addEventListener('shopify:block:deselect', () => this.start());
      }

      this.scheduleInitialAutoplay();
    }

    disconnectedCallback() {
      this.stop();
      if (this.initialAutoplayTimer) {
        clearTimeout(this.initialAutoplayTimer);
        this.initialAutoplayTimer = null;
      }
    }

    go(next) {
      const total = this.slides.length;
      this.index = (next + total) % total;
      this.loadSlide(this.index);
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
      this.activateVideo(this.index);
      this.deferAdjacentSlide();
    }

    /* Runs once, only for a video sitting in slide 0. The poster image is
       already visible (loaded eagerly above), so the very first paint is
       never blocked on the video — but waiting for the full window 'load'
       event (every image, font, app embed and tracking script on the
       page) before starting it made visitors stare at a static photo for
       several seconds, which felt worse than the small bandwidth trade-off
       of starting the (now much smaller, WebM-first) video a little
       earlier. A short fixed delay is enough to let the critical above-
       the-fold request (poster image, fonts, CSS) get a head start,
       without waiting on everything else the page happens to load. */
    deferFirstVideo() {
      window.setTimeout(() => this.activateVideo(this.index), 600);
    }

    /* Videos are heavy, so unlike images (which get a deferred preload one
       slide ahead) a slide's <video> only starts downloading the moment its
       slide actually becomes active — never before, never for slides the
       visitor doesn't reach. iOS Safari in particular keeps decoded video
       memory allocated per <video> element for as long as it has a loaded
       src, even while paused — with several video slides in one autoplaying
       carousel, leaving each one "loaded" as the visitor scrolls past piles
       up enough memory to crash the tab (Safari silently reloads the page
       when that happens). So a slide that isn't active gets its video fully
       released, not just paused — data-src is kept around (never deleted)
       so it can be reloaded cheaply if the visitor comes back to it. */
    activateVideo(index) {
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video[data-hero-lazy-video]');
        if (!video) return;

        if (i !== index) {
          this.unloadVideo(video);
          return;
        }

        this.loadVideoSources(video);

        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            /* Autoplay can still be refused (e.g. Low Power Mode) — the
               poster frame stays visible, which is an acceptable fallback. */
          });
        }
      });
    }

    /* Points a lazy video's <video src> and/or <source src> at its
       data-src (data-src is left in place so this can run again later). */
    loadVideoSources(video) {
      let needsLoad = false;

      if (video.dataset.src && video.getAttribute('src') !== video.dataset.src) {
        video.src = video.dataset.src;
        needsLoad = true;
      }

      const lazySources = video.querySelectorAll('source[data-src]');
      lazySources.forEach((source) => {
        if (source.getAttribute('src') !== source.dataset.src) {
          source.src = source.dataset.src;
          needsLoad = true;
        }
      });

      if (needsLoad) video.load();
    }

    /* Releases a video's decoded memory once its slide is no longer
       active — pausing alone isn't enough on iOS Safari (see above). */
    unloadVideo(video) {
      if (!video.paused) video.pause();

      const loadedSources = video.querySelectorAll('source[src]');
      if (!video.hasAttribute('src') && !loadedSources.length) return; // already unloaded

      video.removeAttribute('src');
      loadedSources.forEach((source) => source.removeAttribute('src'));
      video.load();
    }

    loadSlide(index) {
      const slide = this.slides[index];
      if (!slide || slide.dataset.mediaLoaded === 'true') return;

      slide.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      });

      slide.querySelectorAll('img[data-hero-lazy-image]').forEach((image) => {
        if (image.dataset.srcset) {
          image.srcset = image.dataset.srcset;
          image.removeAttribute('data-srcset');
        }
        if (image.dataset.src) {
          image.src = image.dataset.src;
          image.removeAttribute('data-src');
        }
        image.removeAttribute('data-hero-lazy-image');
      });

      slide.dataset.mediaLoaded = 'true';
    }

    deferAdjacentSlide() {
      if (this.slides.length < 2) return;
      const nextIndex = (this.index + 1) % this.slides.length;
      const loadNext = () => this.loadSlide(nextIndex);

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(loadNext, { timeout: 1800 });
        return;
      }

      window.setTimeout(loadNext, 1200);
    }

    scheduleInitialAutoplay() {
      if (!this.autoplay || this.initialAutoplayTimer) return;

      this.initialAutoplayTimer = window.setTimeout(() => {
        this.initialAutoplayTimer = null;
        this.autoplayReady = true;

        if (!this.isPaused) {
          this.go(this.index + 1);
          this.start();
        }
      }, this.initialAutoplayDelay);
    }

    start() {
      if (!this.autoplay || !this.autoplayReady || this.isPaused) return;
      this.stop();
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }

    stop() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    /* Unified drag handling via Pointer Events — covers touch swipe on
       phones/tablets AND click-drag with a mouse on desktop in one
       implementation (Pointer Events represent touch, mouse and pen
       through the same API). */
    setupDrag() {
      if (!this.track) return;

      const threshold = 40; // how far a gesture must travel to change slides
      const captureThreshold = 10; // how far before we treat it as a drag at all
      let startX = 0;
      let deltaX = 0;
      let pointerId = null;
      let tracking = false; // pointer is down, watching to see if it becomes a drag
      let dragging = false; // movement passed captureThreshold — pointer capture is on
      /* Some slides have a link/button that covers most or all of the
         slide (e.g. a full-bleed "click anywhere" video banner). A plain
         click/tap there must reach the link completely untouched (so we
         never capture the pointer for it), but a swipe that happens to
         start on top of it should still change slides instead of
         navigating — decided by whether the pointer actually travels past
         captureThreshold before it's released. */
      let suppressClick = false;

      this.track.style.touchAction = 'pan-y';

      const onPointerDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        tracking = true;
        dragging = false;
        pointerId = e.pointerId;
        startX = e.clientX;
        deltaX = 0;
      };

      const onPointerMove = (e) => {
        if (!tracking || e.pointerId !== pointerId) return;
        deltaX = e.clientX - startX;
        if (!dragging && Math.abs(deltaX) > captureThreshold) {
          dragging = true;
          this.stop();
          if (this.track.setPointerCapture) {
            try {
              this.track.setPointerCapture(pointerId);
            } catch (err) {
              /* ignore */
            }
          }
        }
      };

      const onPointerEnd = (e) => {
        if (!tracking || e.pointerId !== pointerId) return;
        tracking = false;
        if (dragging) {
          dragging = false;
          if (Math.abs(deltaX) > threshold) {
            suppressClick = true;
            this.go(this.index + (deltaX < 0 ? 1 : -1));
          }
          this.start();
          if (this.track.releasePointerCapture) {
            try {
              this.track.releasePointerCapture(pointerId);
            } catch (err) {
              /* ignore */
            }
          }
        }
      };

      this.track.addEventListener('pointerdown', onPointerDown);
      this.track.addEventListener('pointermove', onPointerMove);
      this.track.addEventListener('pointerup', onPointerEnd);
      this.track.addEventListener('pointercancel', onPointerEnd);

      /* Capture phase runs before the click reaches the link/button itself,
         so a swipe that happened to start on top of one never triggers its
         navigation — only a genuine tap (no meaningful movement) does. */
      this.track.addEventListener(
        'click',
        (e) => {
          if (!suppressClick) return;
          suppressClick = false;
          e.preventDefault();
          e.stopPropagation();
        },
        true
      );
    }

    /* Two-finger trackpad swipe on desktop — trackpads report a
       horizontal scroll (wheel deltaX) rather than touch/pointer events. */
    setupWheel() {
      if (!this.track) return;
      let locked = false;

      this.track.addEventListener(
        'wheel',
        (e) => {
          if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || Math.abs(e.deltaX) < 12) return;
          e.preventDefault();
          if (locked) return;
          locked = true;
          this.go(this.index + (e.deltaX > 0 ? 1 : -1));
          setTimeout(() => {
            locked = false;
          }, 600);
        },
        { passive: false }
      );
    }
  }

  customElements.define('hero-slider-carousel', HeroSliderCarousel);
}